import { format } from 'date-fns';

import { calculateTariffDuty } from 'api/bitedb/tariffs/tariffs.service';

export { effectiveBaseDutyRate, engineFigures } from './tariffFigures';
export type { EngineFigures, EngineMath } from './tariffFigures';

// ---------------------------------------------------------------------------
// The US duty engine runs on the server: POST /tariff/calculate on the
// biteservices analyzer, the same engine (services/shared/src/utils/
// tariffCalculation.util.ts) the ACE Analyzer runs. The five tariff
// components no longer carry a copy, so there is no local fallback — a failed
// call is reported to the user, not silently priced some other way.
//
// Duty, MPF, HMF and the totals all come from the engine's `math`. Nothing in
// the client parses a rate string to produce a dollar figure.
//
// This module exists so the *adapter* does not become the sixth copy. Five
// components need the same input mapping and the same result merge; writing it
// into each of them would reproduce, one layer up, exactly the duplication the
// migration removed.
// ---------------------------------------------------------------------------

/**
 * A date picker's value as the calendar day the user picked ("YYYY-MM-DD").
 *
 * The pickers hold local midnight. Sent as an instant, that is the previous
 * day in Eastern Time for anyone east of New York, and the engine compares
 * rule windows and MPF fiscal years as Eastern calendar days. A bare date is
 * taken literally.
 */
const calendarDay = (value: unknown): string | undefined => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? undefined
      : format(value, 'yyyy-MM-dd');
  }
  return typeof value === 'string' && value ? value : undefined;
};

/** The keys a toggle map has switched on. */
export const enabledKeys = (
  m: Record<string, boolean> | undefined,
): string[] => Object.keys(m || {}).filter((k) => Boolean(m && m[k]));

/** The keys a toggle map has explicitly switched off. */
export const declinedKeys = (
  m: Record<string, boolean> | undefined,
): string[] => Object.keys(m || {}).filter((k) => m?.[k] === false);

/**
 * Restore the material percentage used by the tariff controls.
 *
 * Saved tariff snapshots already use the controls' 0-100 scale, including
 * values below 1 (0.5 means 0.5%). Product DB's legacy `material_pct` column
 * uses a 0-1 fraction, so only that fallback is eligible for conversion.
 */
export const hydrateMaterialPct = (
  savedPercentage: unknown,
  storedFraction: unknown,
  fallback = 40,
): number => {
  if (
    savedPercentage !== undefined &&
    savedPercentage !== null &&
    savedPercentage !== ''
  ) {
    const saved = Number(savedPercentage);
    if (Number.isFinite(saved)) {
      return saved;
    }
  }

  if (
    storedFraction !== undefined &&
    storedFraction !== null &&
    storedFraction !== ''
  ) {
    const stored = Number(storedFraction);
    if (Number.isFinite(stored)) {
      return stored > 0 && stored <= 1 ? stored * 100 : stored;
    }
  }

  return fallback;
};

/**
 * The engine's inputs, from a component's state.
 *
 * Toggle state rides in two fields and nothing else: `claimedCh99Codes` is the
 * exclusion map (the engine builds its whole enabledExclusions map from it and
 * nothing else), and `spiCode` is the FTA map — already keyed by SPI code, so
 * it maps straight across. That is what lets a toggle be a re-post rather than
 * a second implementation of the candidate build.
 *
 * `loadingCountry` is passed separately because the components hold it in its
 * own state, not on `tariffInputs`. The engine reads it as `countryOfSmeltPour`
 * (`loadingCountry = countryOfSmeltPour || originCountry`), and rules gate on
 * it — so omitting it silently falls back to the origin country and the user's
 * selection is ignored.
 *
 * `extra` carries the rest of what a component holds outside `tariffInputs`:
 * - the entry and loading dates, which live in their own date-picker state.
 *   Left out, the engine prices the line as of today and fails every "loaded
 *   before" criterion, whatever dates the form shows;
 * - the "hide IEEPA" toggle, which drops IEEPA rules on the server;
 * - the filer's answers to import-ban gate questions. An unanswered gate keeps
 *   its ban on, and the engine returns the ban alone while one is;
 * - whether `materialPct` is already a 0-100 percentage. Unflagged, the engine
 *   reads anything at or below 1 as a fraction, so 0.5% typed into a material
 *   control prices as 50%. A view whose value can be a stored 0-1 fraction
 *   (the Product DB's material_pct) leaves it unset and keeps that reading;
 * - whether to send the exclusions switched off in `excls` as
 *   `declinedCh99Codes`. The engine never auto-applies a declined exclusion, so
 *   switching off one it elected removes it. Only a view whose `false` entries
 *   are all the user's own choices (the Tariff Tool) sets it.
 */
export const buildEngineInputs = (
  tariffInputs: any,
  productValue: number,
  amountInputs: Record<string, number> | undefined,
  materialPctByKey: Record<string, number> | undefined,
  ftas?: Record<string, boolean>,
  excls?: Record<string, boolean>,
  loadingCountry?: string,
  extra?: {
    entryDate?: unknown;
    loadingDate?: unknown;
    excludeIeepa?: boolean;
    importBanAnswers?: Record<string, string>;
    materialPctIsPercent?: boolean;
    sendDeclined?: boolean;
  },
): any => ({
  htsCode: tariffInputs?.htsCode,
  // The engine keys Column 2, FTA eligibility and the HTS record on the ISO-2
  // code; the forms hold the display name in originCountry.
  originCountry: tariffInputs?.originIso2 || tariffInputs?.originCountry,
  productValue: productValue || 0,
  // The forms default to Ocean and charge HMF on that assumption; the engine
  // only charges HMF when told the line is ocean.
  transportMethod: tariffInputs?.transportMethod || 'Ocean',
  entryDate: calendarDay(extra?.entryDate ?? tariffInputs?.entryDate),
  loadingDate: calendarDay(extra?.loadingDate ?? tariffInputs?.loadingDate),
  materialPct: tariffInputs?.materialPct,
  materialPctIsPercent: extra?.materialPctIsPercent,
  materialPctByKey: materialPctByKey,
  amountValue: tariffInputs?.amountValue,
  amountUnit: tariffInputs?.amountUnit,
  amountInputs: amountInputs,
  countryOfSmeltPour: loadingCountry || tariffInputs?.countryOfSmeltPour,
  spiCode: ftas ? enabledKeys(ftas).join(',') : tariffInputs?.spiCode,
  claimedCh99Codes: excls ? enabledKeys(excls) : undefined,
  declinedCh99Codes:
    excls && extra?.sendDeclined ? declinedKeys(excls) : undefined,
  excludeIeepa: Boolean(extra?.excludeIeepa),
  importBanAnswers: extra?.importBanAnswers,
});

/**
 * Overwrite only the fields the engine owns, leaving everything a component
 * derived for itself (the AI explanation, HTS history, ADCVD) in place. Merging rather than replacing means a field this
 * mapping forgets degrades to the local value instead of vanishing from the UI.
 *
 * `ftaOptions` is deliberately NOT mapped. The engine has a field of that name
 * — the FTA subset of its optional exclusions, i.e. Candidates — but the UI's
 * `dataObj.ftaOptions` is a different thing that happens to share the name:
 * `{ code, name }` pairs parsed from the HTS record's own special-rate field,
 * which is what the FTA toggles render. Mapping one onto the other replaces the
 * toggle list with candidate objects and the switches stop working. That
 * derivation is HTS-document parsing, not duty math, and stays client-side.
 */
export const applyEngineResult = (
  dataObj: any,
  remote: any,
  productValue: number,
): void => {
  const addlDollars = Number(remote?.math?.additionalDutyDollars) || 0;
  const addlPct = productValue ? (addlDollars / productValue) * 100 : 0;

  dataObj.baseDutyRate = remote?.base?.baseDutyRate ?? dataObj.baseDutyRate;
  dataObj.baseDutyRateRaw =
    remote?.base?.baseDutyRateRaw ?? dataObj.baseDutyRateRaw;
  dataObj.lineSplitLabel =
    remote?.base?.lineSplitLabel ?? dataObj.lineSplitLabel;
  dataObj.results = remote?.results?.summaryRows ?? dataObj.results;
  dataObj.lines = remote?.results?.lines ?? dataObj.lines;
  dataObj.lineSplitFields =
    remote?.results?.splitShares ?? dataObj.lineSplitFields;
  dataObj.optionalExclusions =
    remote?.results?.optionalExclusions ?? dataObj.optionalExclusions;
  dataObj.requiredAmountUnits =
    remote?.results?.requiredAmountUnits ?? dataObj.requiredAmountUnits;
  // Preserve the ban metadata too. Consumers use it to render the gate
  // question and must never mistake a barred, $0 response for a free entry.
  dataObj.importBans = remote?.results?.importBans ?? dataObj.importBans;
  dataObj.banGateAnswers =
    remote?.results?.banGateAnswers ?? dataObj.banGateAnswers;
  // `null` is meaningful here: it says a submitted gate answer lifted the
  // previous ban. Preserve only an absent field, not an explicit null.
  if (
    remote?.results &&
    Object.prototype.hasOwnProperty.call(remote.results, 'activeImportBan')
  ) {
    dataObj.activeImportBan = remote.results.activeImportBan;
  }
  dataObj.additionalDutyRate = `${addlPct.toFixed(2).replace(/\.00$/, '')}%`;
  // The specific (per-unit) portion of base duty, which recomputeFromState
  // used to publish alongside the rate.
  dataObj.baseAmount = Number(remote?.math?.baseAmountDollars) || 0;
  // The engine's own totals — duty, MPF, HMF and total cost — kept on the
  // result so every consumer reads one figure rather than recomputing it.
  dataObj.engineMath = remote?.math ?? dataObj.engineMath;
  // Offer the "hide IEEPA" toggle when IEEPA applies, and keep offering it
  // while it is hidden, when no IEEPA code is left in the result.
  dataObj.hasIeepaTariffs = Boolean(
    remote?.results?.ieepaApplies || remote?.results?.ieepaExcluded,
  );
};

/**
 * What runEngine returns when a later call has taken over the render. The
 * caller must apply nothing from it — not even form state.
 */
export const SUPERSEDED = Object.freeze({ superseded: true });

/** A mutable counter, held by the component in a ref. */
export interface EngineSeq {
  current: number;
}

/**
 * Call the server engine and, unless `logOnly`, render its answer.
 *
 * Returns the engine payload when the server answered. `null` means the call
 * failed; the caller tells the user, since there is no local engine to fall
 * back to. SUPERSEDED means a later call owns the render.
 *
 * The payload is returned, not just a flag, because a caller that has removed
 * its local pipeline needs fields `applyEngineResult` does not copy onto
 * `dataObj` — `results.candidates`, `needsLoadingCountry`, `countryKeyCodes` —
 * to decide which inputs the form should show.
 */
export const runEngine = async (args: {
  inputs: any;
  current: any;
  productValue: number;
  seqRef: EngineSeq;
  label: string;
  logOnly: boolean;
  setData: (data: any) => void;
}): Promise<any | null> => {
  const { inputs, current, productValue, seqRef, label, logOnly, setData } =
    args;

  // Toggling twice quickly issues two calls, and they can land out of order.
  // Without this the older answer overwrites the newer one and the panel
  // disagrees with the switches the user is looking at.
  const seq = ++seqRef.current;

  try {
    const remote = await calculateTariffDuty(inputs);

    if (logOnly) {
      return null;
    }
    if (seq !== seqRef.current) {
      // Superseded by a later call, which owns the render. Returning the stale
      // payload let callers apply it as form state or results anyway.
      return SUPERSEDED;
    }

    const next = { ...(current || {}) };
    applyEngineResult(next, remote, productValue);
    setData(next);
    return remote;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`[tariff-engine] server calc failed — ${label}`, e);
    return null;
  }
};
